/**
 * 运营指挥中心左侧展示页面
 * 全屏、深色主题、定时刷新数据
 *
 * 核心功能：
 * 1. 30秒定时轮询所有数据（日期窗口随每次取数实时计算，自然跨天）
 * 2. 顶部时钟每秒更新；时间轴基准时间每分钟更新
 */
import React, { useState, useEffect, useCallback, useMemo } from "react";
import dayjs from "dayjs";
import utc from "dayjs/plugin/utc";
import timezone from "dayjs/plugin/timezone";
import { message } from "antd";
import useInterval from "../../../hooks/useInterval";
import { screenService } from "../../../api/enhancedApi";
import FleetStatusChart from "./components/FleetStatusChart";
import FlightTimeline from "./components/FlightTimeline";
import "./ScreenLeftPage.css";

dayjs.extend(utc);
dayjs.extend(timezone);

const ScreenLeftPage = () => {
    // ===== 状态管理 =====
    // currentTime 按分钟更新，驱动时间轴（避免时间轴每秒重渲染）
    const [currentTime, setCurrentTime] = useState(dayjs());
    // clockTime 按秒更新，仅用于顶部时钟显示
    const [clockTime, setClockTime] = useState(dayjs());
    const [loading, setLoading] = useState(true);
    const [lastUpdateTime, setLastUpdateTime] = useState(null); // 最后一次全部数据刷新成功的时间
    const [errorCount, setErrorCount] = useState(0); // 连续出现数据源失败的轮询次数

    // 数据状态
    const [fleetStatus, setFleetStatus] = useState({ items: [], total: 0 });
    const [flightTimeline, setFlightTimeline] = useState({ items: [], total: 0 });
    const [maintenanceEvents, setMaintenanceEvents] = useState({}); // 维护事件 {aircraft_reg: [...]}

    // ===== 数据获取函数 =====

    /**
     * 获取机队技术状态数据（包含配置和实际运行数据）
     */
    const fetchFleetStatus = useCallback(async () => {
        try {
            const response = await screenService.getFleetTechnicalStatus();
            setFleetStatus(response.data || { items: [], total: 0 });
            return true;
        } catch (error) {
            console.error("[展示页] 获取机队状态失败:", error);
            return false;
        }
    }, []);

    /**
     * 获取飞行时间轴数据（未来3天）
     */
    const fetchFlightTimeline = useCallback(async () => {
        try {
            // 计算北京时间今天00:00 - 后天23:59
            const bjTime = dayjs().tz("Asia/Shanghai");
            const start = bjTime.startOf("day").utc().format();
            const end = bjTime.add(2, "day").endOf("day").utc().format();

            const response = await screenService.getFlightTimeline({
                start_time: start,
                end_time: end,
            });

            setFlightTimeline(response.data || { items: [], total: 0 });
            return true;
        } catch (error) {
            console.error("[展示页] 获取航班时间轴失败:", error);
            return false;
        }
    }, []);

    /**
     * 获取维护事件数据（用于时间轴展示）
     */
    const fetchMaintenanceEvents = useCallback(async () => {
        try {
            // 计算北京时间今天 - 后天
            const bjTime = dayjs().tz("Asia/Shanghai");
            const startDate = bjTime.startOf("day").format("YYYY-MM-DD");
            const endDate = bjTime.add(2, "day").endOf("day").format("YYYY-MM-DD");

            // 获取时间轴上配置的飞机列表
            const aircraftRegs = flightTimeline.items?.map(a => a.aircraft_registration) || [];
            if (aircraftRegs.length === 0) {
                setMaintenanceEvents({});
                return true;
            }

            const response = await screenService.getDisplayMaintenanceEvents({
                aircraft_registrations: aircraftRegs.join(","),
                start_date: startDate,
                end_date: endDate,
            });

            const eventsData = response.data?.data || {};
            setMaintenanceEvents(eventsData);
            return true;
        } catch (error) {
            console.error("[展示页] 获取维护事件失败:", error);
            return false;
        }
    }, [flightTimeline.items]);

    /**
     * 获取所有数据（用于定时轮询）
     */
    const fetchAllData = useCallback(async (isInitial = false) => {
        if (isInitial) {
            setLoading(true);
        }

        try {
            const results = await Promise.all([
                fetchFleetStatus(),
                fetchFlightTimeline()
            ]);

            // 任一数据源失败即计入异常，避免部分数据坏了却不报警
            if (results.some(r => r === false)) {
                setErrorCount(prev => prev + 1);
                if (isInitial && results.every(r => r === false)) {
                    message.error("数据加载失败，请刷新页面重试");
                }
            } else {
                setErrorCount(0);
                setLastUpdateTime(dayjs());
            }
        } catch (error) {
            console.error("[展示页] 刷新数据失败:", error);
            setErrorCount(prev => prev + 1);
            if (isInitial) {
                message.error("数据加载失败，请刷新页面重试");
            }
        } finally {
            if (isInitial) {
                setLoading(false);
            }
        }
    }, [fetchFleetStatus, fetchFlightTimeline]);

    // 当时间轴数据更新后，获取维护事件
    useEffect(() => {
        if (flightTimeline.items && flightTimeline.items.length > 0) {
            fetchMaintenanceEvents();
        }
    }, [flightTimeline.items, fetchMaintenanceEvents]);

    // ===== 定时任务 =====

    // 30秒定时轮询所有数据
    useInterval(
        () => fetchAllData(false),
        30000,
        false, // 不立即执行（初始化时已经执行过）
    );

    // 每分钟更新时间轴基准时间
    // 跨天切换无需单独处理：30秒轮询取数和时间轴窗口都基于当前时间实时计算
    useInterval(
        () => {
            setCurrentTime(dayjs());
        },
        60000,
        true, // 立即执行一次
    );

    // 每秒更新顶部时钟
    useInterval(
        () => {
            setClockTime(dayjs());
        },
        1000,
        true,
    );

    // ===== 初始化 =====
    useEffect(() => {
        fetchAllData(true); // 初始加载
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    // ===== 机队卡片数据 =====
    // 显示哪些飞机、按什么顺序，由控制页"大屏飞机配置"决定，后端已按 display_order 排序返回。
    // useMemo 保持数组引用稳定，避免顶部时钟每秒触发的重渲染重置卡片轮播计时器
    const fleetItems = useMemo(() => fleetStatus.items || [], [fleetStatus.items]);

    // ===== 渲染 =====
    return (
        <div className="screen-left-page">
            {/* 顶部标题栏 */}
            <header className="screen-header">
                <h1 className="screen-title text-neon">运营指挥中心</h1>
                <div className="screen-status">
                    <div className="status-item">
                        <span className="status-label">刷新模式:</span>
                        <span className="status-value status-connected">自动轮询 (30秒)</span>
                    </div>
                    <div className="status-item">
                        <span className="status-label">当前时间:</span>
                        <span className="status-value">{clockTime.format("HH:mm:ss")}</span>
                    </div>
                    {lastUpdateTime && (
                        <div className="status-item">
                            <span className="status-label">最后更新:</span>
                            <span className="status-value">{lastUpdateTime.format("HH:mm:ss")}</span>
                        </div>
                    )}
                    {errorCount > 0 && (
                        <div className="status-item status-error">
                            <span className="status-label">⚠️ 数据刷新异常</span>
                            <span className="status-value">连续失败 {errorCount} 次</span>
                        </div>
                    )}
                </div>
            </header>

            {/* 主内容区 */}
            <main className="screen-content">
                {loading ? (
                    <div className="screen-loading">
                        <div className="loading-spinner"></div>
                        <p>数据加载中...</p>
                    </div>
                ) : (
                    <>
                        {/* 区域1：飞行进程时间轴 */}
                        <section className="screen-section section-timeline">
                            <div className="section-header">
                                <h2 className="section-title">机队技术状态监控</h2>
                            </div>
                            <FlightTimeline 
                                data={flightTimeline.items} 
                                maintenanceEvents={maintenanceEvents}
                                currentTime={currentTime} 
                            />
                        </section>

                        {/* 区域2：机队卡片 */}
                        <section className="screen-section section-fleet">
                            <FleetStatusChart data={fleetItems} loading={loading} />
                        </section>
                    </>
                )}
            </main>
        </div>
    );
};

export default ScreenLeftPage;
